import { ElementRef, FocusEvent, forwardRef, KeyboardEvent, memo, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown } from 'primereact/dropdown';
import { InputDate } from 'medere-ui-framework';
import { PERIOD_PRESETS, PeriodChoice } from './periodRange';
import './FilterParts.scss';

/** Una de las dos fechas del período personalizado. */
export type PeriodDateField = 'from' | 'to';

export interface PeriodFieldProps {
    /** Prefijo de los ids de los campos (`ss` da `ss-period`), para que haya uno por pantalla. */
    idPrefix: string;
    period: PeriodChoice;
    /** "25/9 al 24/10": el rango del atajo elegido, en texto chico bajo «Período» (vacío con «Personalizado»). */
    rangeText: string;
    from: Date | null;
    to: Date | null;
    /** Errores de cada fecha, para mostrar en su campo. */
    errors: Partial<Record<PeriodDateField, string>>;
    /**
     * Una fecha completa y válida, escrita o elegida en el calendario (o `null` con «Limpiar»): se guarda y se aplica ya.
     * Tiene que ser estable entre renderizados (ver `DateInput`).
     */
    onDateChange: (field: PeriodDateField, value: Date | null) => void;
    /** Al salir de los campos de fecha o con Enter: vuelve a validar (no hace nada si no hay nada pendiente). */
    onDatesCommit: () => void;
    onPeriodChange: (choice: PeriodChoice) => void;
}

const PERIOD_LABEL_ID: Record<PeriodChoice, string> = {
    today: 'PeriodFieldPresetToday',
    week: 'PeriodFieldPresetWeek',
    month30: 'PeriodFieldPresetMonth30',
    thisMonth: 'PeriodFieldPresetThisMonth',
    lastMonth: 'PeriodFieldPresetLastMonth',
    last3Months: 'PeriodFieldPresetLast3Months',
    thisYear: 'PeriodFieldPresetThisYear',
    custom: 'PeriodFieldPresetCustom',
};

const PERIOD_CHOICES: PeriodChoice[] = [...PERIOD_PRESETS, 'custom'];

type InputDateHandle = ElementRef<typeof InputDate>;

/** Lo que informa el `InputDate` al cambiar: `originalEvent.type` es `input` si se escribió y otro (`click`, `keydown`) si se eligió en el calendario. */
interface DateChangeEvent {
    value?: Date | null;
    originalEvent?: { type?: string };
}

interface DateInputProps {
    field: PeriodDateField;
    label: string;
    value: Date | null;
    error?: string;
    onChange: (field: PeriodDateField, event: DateChangeEvent) => void;
}

/**
 * Un campo de fecha. Va memoizado y con todas las props estables a propósito: el calendario de PrimeReact rehace todas
 * sus celdas cuando se vuelve a renderizar el `InputDate`, y un renderizado entre el mousedown y el mouseup de un día
 * (la carga de datos, un estado de la pantalla) hace que el click se pierda: «hay que hacer doble click». Con las props
 * iguales, ningún renderizado de la pantalla toca al calendario abierto.
 * `notFocusNextfield`: el campo del framework, al elegir una fecha, manda el foco al «siguiente» input del DOM (y abre su
 * calendario); acá el foco lo maneja `PeriodField`.
 */
const DateInput = memo(forwardRef<InputDateHandle, DateInputProps>(function DateInput({ field, label, value, error, onChange }, ref) {
    const handleChange = useCallback((event: DateChangeEvent) => onChange(field, event), [field, onChange]);
    return (
        <InputDate ref={ref} label={label} required className="nv-period-date" value={value} notFocusNextfield
            onChange={handleChange} error={!!error} caption={error} />
    );
}));

/**
 * «Período»: un selector con Hoy, Últimos 7 días, Últimos 30 días, Mes actual, Mes anterior, Últimos 3 meses, Este año y Personalizado, con el rango
 * resuelto debajo. Las fechas Desde y Hasta solo aparecen con «Personalizado» y se aplican en cuanto hay una fecha
 * completa y válida (escrita o elegida en el calendario: un solo gesto); los atajos, al elegir. No hay «Consultar».
 * Va dentro de una fila de filtros (`nv-form-row`): son uno o dos hijos de esa fila.
 */
export default function PeriodField({
    idPrefix, period, rangeText, from, to, errors, onDateChange, onDatesCommit, onPeriodChange,
}: PeriodFieldProps) {
    const intl = useIntl();

    const periodOptions = useMemo(() => PERIOD_CHOICES.map(value => ({
        value, label: intl.formatMessage({ id: PERIOD_LABEL_ID[value] }),
    })), [intl]);

    const fromRef = useRef<InputDateHandle>(null);
    const toRef = useRef<InputDateHandle>(null);
    // Lo último que hay en «Hasta», para saber (sin recrear el callback de los campos) si un «Desde» elegido lo pasa
    const lastTo = useRef(to);
    lastTo.current = to;

    // Las fechas se aplican en cuanto hay una completa y válida (escrita o elegida en el calendario): un solo gesto basta.
    // Al salir de los campos o con Enter solo se vuelve a validar, que si no hay nada pendiente no cambia nada: ese
    // salir ocurre en el mousedown sobre el calendario, y cualquier renderizado ahí se come el click del día.
    const handleDatesBlur = (event: FocusEvent<HTMLDivElement>) => {
        const next = event.relatedTarget as Element | null;
        // El calendario se monta en el body, fuera del contenedor de los campos, pero sigue siendo parte de ellos
        if (!event.currentTarget.contains(next) && !next?.closest('.p-datepicker')) onDatesCommit();
    };
    const handleDatesKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') onDatesCommit();
    };
    /**
     * Mientras se escribe, el campo avisa "sin fecha" en cada tecla, y devolverle ese null lo borra y se come las teclas
     * siguientes: de lo escrito solo vale una fecha completa (un texto a medias vuelve a la última fecha al salir).
     * Lo elegido en el calendario (día, «Hoy», «Limpiar») vale siempre. Escrito, el foco se queda donde está; elegido, el
     * calendario ya se cerró y el foco vuelve al botón del calendario del mismo campo, salvo que «Desde» haya quedado
     * después de «Hasta»: ahí se pasa a «Hasta» (con su calendario abierto) para corregirlo.
     */
    const handleDateChange = useCallback((field: PeriodDateField, event: DateChangeEvent) => {
        const value = event.value ?? null;
        const typed = event.originalEvent?.type === 'input';
        if (typed && value === null) return;
        onDateChange(field, value);
        if (typed) return;
        const own = field === 'from' ? fromRef.current : toRef.current;
        const orderBroken = field === 'from' && value !== null && lastTo.current !== null && value > lastTo.current;
        window.setTimeout(() => {
            if (orderBroken) toRef.current?.focus();
            else own?.refs.containerRef.current?.querySelector<HTMLElement>('.p-datepicker-trigger')?.focus();
        });
    }, [onDateChange]);

    const periodId = `${idPrefix}-period`;
    const rangeId = `${periodId}-range`;

    return (
        <>
            <div className="nv-field nv-w-s">
                <label htmlFor={periodId}>{intl.formatMessage({ id: 'PeriodFieldLabel' })}</label>
                <Dropdown inputId={periodId} value={period} options={periodOptions}
                    optionLabel="label" optionValue="value" aria-describedby={rangeText ? rangeId : undefined}
                    onChange={e => onPeriodChange(e.value)} />
                {rangeText && <small id={rangeId} className="nv-help">{rangeText}</small>}
            </div>
            {period === 'custom' && (
                <div className="nv-form-row" onBlur={handleDatesBlur} onKeyDown={handleDatesKeyDown}>
                    <DateInput ref={fromRef} field="from" label={intl.formatMessage({ id: 'PeriodFieldFrom' })}
                        value={from} error={errors.from} onChange={handleDateChange} />
                    <DateInput ref={toRef} field="to" label={intl.formatMessage({ id: 'PeriodFieldTo' })}
                        value={to} error={errors.to} onChange={handleDateChange} />
                </div>
            )}
        </>
    );
}
